import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import FileView from "./FileViewPage";
import { useDarkMode } from "../context/darkModeContext";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  // Input,
  Skeleton,
  TextField,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FileInfo from "../components/files/FileInfo";
import FileShare from "../components/files/FileShare";

import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DoneIcon from "@mui/icons-material/Done";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";

import fileContext from "../context/fileContext";
import { api } from "../utils/axios-instance";
import useAuth from "../stores/authStore";
import toast, { Toaster } from "react-hot-toast";
import DeleteAlert from "../components/RecycleBin/DeleteAlert";
import { RenameFile } from "../components/RenameFile";
import { fileIcons } from "../utils/icon-mimetypes";
import { SetPassword } from "../components/dialogs/SetPassword";
import { auth, storage } from "../helper/firebaseClient";
import { ref, getDownloadURL } from "firebase/storage";
import axios from "axios";
import { saveAs } from "file-saver";
import FileBg from "../generators/fileBGGenerator";
import { ShowVersions } from "../components/dialogs/ShowVersions";
import { useFileStore } from "../store/fileStore";

// images from public folder
const threeDots = `${process.env.PUBLIC_URL}/assets/threedots.webp`;
const DefaultIcon = `${process.env.PUBLIC_URL}/assets/defaulticon.webp`;

const FilesPage = ({
  filteredData,
  loading,
  select,
  setSelect,
  showMultiFileOptions,
  setShowMultiFileOptions,
  removeFile,
  value,
  updateFiles,
  deptName,
  setFile,
  folderId = undefined,
  onFileDeleted, // called with fileId when a single file is recycled
}) => {
  const location = useLocation();
  const { darkMode } = useDarkMode();

  const [selectedFileInfo, setSelectedFileInfo] = useState({});

  //debug for deleted file
  const [recycleBinLoading, setRecycleBinLoading] = useState(true);
  const [sharedFileInfo, setSharedFileInfo] = useState({});
  const [isFileViewOpen, setIsFileViewOpen] = useState(false);
  const [isFileInfoOpen, setIsFileInfoOpen] = useState(false);
  const [fileAuthenticated, setFileAuthenticated] = useState(false);

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [checkPasswordLoading, setCheckPasswordLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");

  // const [anchorEl, setAnchorEl] = useState(null);
  const context = useContext(fileContext);
  const {
    anchorEl,
    setAnchorEl,
    updateFilesState,
    updateDepartmentFiles,
    setOpenInChat,
    setSelectedFiles,
  } = context;

  const [menuFile, setMenuFile] = useState({});
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const isSelected = (file) => selectedFileIds.includes(file.id);

  const profileData = useAuth((state) => state.profileData);

  const [recycleFiles, setRecycleFiles] = useState([]);

  const togglePin = useFileStore((state) => state.togglePinLocal);
  const updatePinnedList = useAuth((state) => state.togglePinFileList);

  const toggleLockLocal = useFileStore((state) => state.toggleLockLocal);

  // ── Normalise incoming file objects so FilesPage works regardless of which
  // parent passes the data (DepartmentPage, Dashboard, FolderPage, etc.).
  // Backend stores security flags nested under "security.*" and versioning under
  // "versioning.*". Parents may or may not have already flattened them.
  const normalizeFile = (file) => {
    const meta = file.metadata || {};
    const sec = file.security || {};
    const ver = file.versioning || {};

    // console.log("========== NORMALIZE FILE ==========");
    // console.log("FULL FILE:", file);

    // console.log("META:", file.metadata);
    // console.log("SEC:", file.security);
    // console.log("VER:", file.versioning);

    // console.log("currVersion:", file?.versioning?.curr_version);
    // console.log("download_url:", file?.download_url);

    // console.log("===================================");

    // tiny helper (keeps FilesPage independent from authStore formatter)
    const formatBytes = (bytes) => {
      if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes)))
        return "";
      const b = Number(bytes);
      const units = ["B", "KB", "MB", "GB", "TB"];
      let i = 0;
      let n = b;
      while (n >= 1024 && i < units.length - 1) {
        n /= 1024;
        i += 1;
      }
      return `${n.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
    };

    const resolvedSize =
      file?.size ??
      (file?.metadata?.size !== undefined
        ? formatBytes(file.metadata.size)
        : "");

    const versions = ver.versions ?? [];
    const versionLabel = versions.length > 0 ? `v${versions.length + 1}` : "v1";

    return {
      id: file.id,
      download_url: file.download_url ?? "",
      folder_id: file.folder_id ?? null,
      department_ids: file.department_ids ?? [],
      department: file.department ?? deptName ?? "",
      color: file.color ?? "",

      // top-level identity fields (written directly by backend, NOT inside metadata)
      name: file.name ?? "",
      owner: file.owner_id ?? "",
      profilePic: file.profilePic ?? "",

      // metadata — only size, mimetype, lastModified live here
      size: resolvedSize,
      mimetype: meta.mimetype || meta.mimeType || file.mimetype || "",
      lastUpdate: meta.lastModified ?? "",

      // security
      isLocked: file.isLocked ?? sec.is_locked ?? false,
      lockedBy: file.lockedBy ?? sec.locked_by ?? null,
      lockedByRole: file.lockedByRole ?? sec.locked_by_role ?? null,
      hasPassword: file.hasPassword ?? sec.has_password ?? false,
      password: file.password ?? sec.password_hash ?? null,
      isPinned: file.isPinned ?? false,

      // versioning
      versionId: ver.versionId ?? "",
      // currVersion: ver.curr_version ?? "",
      currVersion: file.currVersion ?? ver.curr_version ?? "",
      versions,
      versionLabel,
    };
  };

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getSharedFileInfo = async (fileId) => {
    try {
      if (!fileId) {
        console.error("Missing fileId");
        return;
      }

      const info = await api.get(`/file/sharedFileInfo/${fileId}/`);
      setSharedFileInfo(info.data);
    } catch (error) {
      console.log(error);
    }
  };

  const openDrawer = (file) => {
    if (!file?.id) {
      console.error("Invalid file object:", file);
      toast.error("Invalid file data. Missing file ID.");
      return;
    }

    getSharedFileInfo(file.id);
    setSelectedFileInfo(file);

    if (file.isLocked) {
      const role = profileData?.role_priv;
      const uid = profileData?.uid || profileData?.id;
      const isOwner = file?.owner_id === uid || file?.owner === uid;

      // org_admin can open any locked file; others only their own
      if (role !== "org_admin" && !isOwner) {
        const lockedByRole =
          file.lockedByRole === "super_admin" ? "super admin" : "org admin";
        toast.error(`File has been locked by ${lockedByRole}!`);
        return;
      }
    }

    if (location.pathname === "/chat") {
      setOpenInChat(true);
      setFile(file);
    } else {
      setIsFileViewOpen(true);
    }
  };

  const closeDrawer = () => {
    setIsFileViewOpen(false);
  };

  const openFileInfoDrawer = (file) => {
    getSharedFileInfo(file.id);
    setSelectedFileInfo(file);
    setIsFileInfoOpen(true);
  };

  const closeFileInfoDrawer = () => {
    setIsFileInfoOpen(false);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const getIconByMimeType = (mimeType) => {
    return fileIcons[mimeType] || DefaultIcon;
  };

  const handleMultiFileSelect = (file) => {
    if (file.isLocked) {
      toast.error("You cannot perform actions on locked files!");
      return;
    }
    setSelectedFileIds((prev) =>
      prev.includes(file.id)
        ? prev.filter((id) => id !== file.id)
        : [...prev, file.id]
    );
  };

  const handleLockChange = async (file) => {
    const role = profileData?.role_priv;
    const uid = profileData?.uid || profileData?.id;
    const isOwner = file?.owner_id === uid || file?.owner === uid;

    // org_admin can lock any file; super_admin and employee only their own
    if (role !== "org_admin" && !isOwner) {
      toast.error("You can only lock/unlock files you own.");
      return;
    }

    const nextLockState = !file?.isLocked;

    // optimistic UI update
    toggleLockLocal(file.id, nextLockState);

    try {
      await api.patch(`/file/change-lock-status/${file.id}/`, {
        is_locked: nextLockState,
        folder_id: folderId,
      });

      toast.success(
        nextLockState
          ? "File locked successfully"
          : "File unlocked successfully"
      );
    } catch (error) {
      console.error(error);

      // rollback
      toggleLockLocal(file.id, !nextLockState);

      toast.error("Failed to update lock status");
    }
  };

  const handlePinChange = async (file) => {
    const fileId = file.id;

    const nextPinnedState = !file?.isPinned;

    // optimistic UI update
    togglePin(fileId, nextPinnedState);

    try {
      await api.patch(`/file/change-pin-status/${fileId}/`, {
        folder_id: folderId,
      });

      updatePinnedList(fileId);

      toast.success(
        nextPinnedState
          ? "File pinned successfully"
          : "File unpinned successfully"
      );
    } catch (error) {
      console.error(error);

      // rollback
      togglePin(fileId, !nextPinnedState);

      toast.error("Failed to update pin status");
    }
  };

  const updateUI = (changedAttr, file, newVal) => {
    const curr_location = location?.pathname.split("/")[1];

    switch (curr_location) {
      case "dashboard":
        updateFilesState(value);
        break;
      case "filesInsideFolder":
        updateFiles(file, changedAttr, newVal);
        break;
      case "department":
        updateDepartmentFiles(deptName);
        break;
      case "chat":
        updateFilesState(value);
        break;
      default:
        console.log("UNEXPECTED INPUT");
    }
  };

  useEffect(() => {
    if (!select) {
      setSelectedFileIds([]);
    }
  }, [select]);

  // Fetch recycle bin data
  useEffect(() => {
    const fetchRecycleBinData = async () => {
      try {
        const response = await api.get("/file/recycle-bin-file");

        if (response && response.data) {
          const deletedFiles = response.data;
          setRecycleFiles(deletedFiles);
        }
      } catch (error) {
        console.error("Error fetching recycle bin files:", error);
      } finally {
        setRecycleBinLoading(false);
      }
    };

    fetchRecycleBinData();
  }, []);

  // FIX: memoize recycle ids
  const recycleFileIds = React.useMemo(() => {
    return new Set((recycleFiles || []).map((f) => f.fileId));
  }, [recycleFiles]);

  // FIX: memoize filteredFiles (and define it BEFORE effects that use it)
  const filteredFiles = React.useMemo(() => {
    return (filteredData || [])
      .filter((file) => !recycleFileIds.has(file.id))
      .map(normalizeFile);
  }, [filteredData, recycleFileIds]);

  // FIX: avoid "Cannot access filteredFiles before initialization"
  useEffect(() => {
    if (selectedFileIds.length > 0 && !showMultiFileOptions) {
      setShowMultiFileOptions(true);
    }

    if (showMultiFileOptions && selectedFileIds.length === 0) {
      setShowMultiFileOptions(false);
      setSelect(false);
    }

    // Convert ids -> actual file objects for menu/actions
    const selectedFiles = filteredFiles.filter((f) =>
      selectedFileIds.includes(f.id)
    );
    setSelectedFiles(selectedFiles);
  }, [
    selectedFileIds,
    filteredFiles,
    showMultiFileOptions,
    setShowMultiFileOptions,
    setSelect,
    setSelectedFiles,
  ]);

  const hexToRgba = (hex, alpha) => {
    let r = 0,
      g = 0,
      b = 0;

    if (!hex) return `rgba(0,0,0,${alpha})`;

    if (hex.length === 4) {
      r = parseInt(hex[1] + hex[1], 16);
      g = parseInt(hex[2] + hex[2], 16);
      b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
      r = parseInt(hex[1] + hex[2], 16);
      g = parseInt(hex[3] + hex[4], 16);
      b = parseInt(hex[5] + hex[6], 16);
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const handlePasswordCheck = async () => {
    setCheckPasswordLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();

      const res = await api.post("/file/verify_password/", {
        fileId: selectedFile.id,
        // filePassword: selectedFile.password,
        userPassword: password,
        idToken: token,
      });

      if (res.data.success) {
        toast.success(res.data.message);
        setFileAuthenticated(true);
        setPasswordDialogOpen(false);

        openDrawer(selectedFile);

        setPassword("");
        setFileAuthenticated(false);
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "Invalid password");
    } finally {
      setCheckPasswordLoading(false);
    }
  };

  const openPasswordDialog = (file) => {
    setSelectedFile(file);
    setPasswordDialogOpen(true);
  };

  const handleRemovePassword = async (file) => {
    try {
      const res = await api.patch(`/file/remove_password/${file.id}/`);
      if (res.data.success) {
        toast.success(res.data.message);
        updateUI("hasPassword", file, false);
      }
    } catch (error) {
      console.log(error);
      toast.error(error.response.data.message || "An error occurred!");
    }
  };

  const logDownloadEvent = async (fileId) => {
    try {
      const token = auth.currentUser && (await auth.currentUser.getIdToken());
      if (!token) return;
      await api.post(`/file/logEvent/${fileId}?event=download`, {
        event: "download",
        idToken: token,
      });
    } catch (error) {
      // Don't block the download UX if logging fails
      console.log("downloadAlert error:", error);
    }
  };

  const handleSingleFileDownload = async (file) => {
    handleClose();
    setDownloadLoading(true);
    try {
      const fileRef = ref(storage, `files/${profileData.org_id}/${file.id}`);
      const downloadURL = await getDownloadURL(fileRef);
      const response = await axios.get(downloadURL, {
        responseType: "arraybuffer",
      });
      const blob = new Blob([response.data], { type: file.mimetype });
      saveAs(blob, file.name.split("_TS=")[0]);
      await logDownloadEvent(file.id);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div>
      {!fileAuthenticated && (
        <Dialog
          open={passwordDialogOpen}
          onClose={() => setPasswordDialogOpen(false)}
          maxWidth="sm"
          sx={{
            borderRadius: "5px",
            zIndex: 9999,
          }}
        >
          <DialogTitle>
            This file is password protected. Please enter the password to view
            the file.
          </DialogTitle>
          <DialogContent
            style={{
              backgroundColor: "#F7F8FA",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
          >
            <TextField
              autoFocus
              id="password"
              name="password"
              type="password"
              value={password}
              variant="outlined"
              label="Password"
              sx={{
                width: "70%",
              }}
              color="secondary"
              placeholder="Enter password"
              onChange={(e) => {
                setPassword(e.target.value);
              }}
            />
          </DialogContent>
          <DialogActions>
            <button
              className={
                "flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed px-4 py-1 rounded-lg shadow-sm text-black border border-gray-700 bg-gray-50 hover:bg-[#D1293Dda] hover:text-white hover:border-[#D1293D]"
              }
              onClick={() => {
                setPassword("");
                setPasswordDialogOpen(false);
                setSelectedFile(null);
              }}
              color="primary"
            >
              Close
            </button>
            <button
              className={
                "flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed px-4 py-1 rounded-lg shadow-sm text-white bg-[#5E5ADB] hover:bg-[#6e6bd0da] gap-x-2"
              }
              onClick={handlePasswordCheck}
              disabled={checkPasswordLoading}
            >
              Submit
              {checkPasswordLoading && (
                <CircularProgress
                  style={{ color: "#fff", height: 15, width: 15 }}
                />
              )}
            </button>
          </DialogActions>
          <Toaster position="bottom-left" reverseOrder={false} />
        </Dialog>
      )}
      <div
        className={`grid ${location.pathname === "/chat" ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"}  gap-4 ${
          darkMode ? "text-gray-200" : "text-gray-600"
        }`}
      >
        {loading
          ? Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="border border-gray-200 p-3 rounded-2xl shadow-sm"
              >
                <span className="flex flex-row justify-between items-center px-1 my-1">
                  <Skeleton variant="circular" height={22} width={22} />
                  <img src={threeDots} alt="..." height={25} width={25} />
                </span>

                <span className="">
                  <Skeleton variant="rounded" height={60} width="100%" />
                  <span className="font-semibold">
                    <Skeleton height={22} width="100%" />
                  </span>
                </span>

                <span>
                  <span className="flex flex-row justify-between items-center my-1 px-1">
                    <span className="w-full text-sm text-gray-500 font-light">
                      <Skeleton height={18} width="70%" />
                    </span>
                    <Skeleton variant="circular" height={22} width={22} />
                  </span>
                </span>
              </div>
            ))
          : filteredFiles.map((file, index) => {
              const color = file.color || "#3498db";
              const backgroundColor = hexToRgba(color, 0.1);

              return (
                <div
                  key={index}
                  className={`border border-[#ffa9ff46] p-3  rounded-[16px] cursor-pointer relative ${location.pathname === "/chat" ? "h-48" : "auto"}`}
                  style={{
                    backgroundColor,
                    backgroundImage: FileBg({
                      gradientColors: [
                        file.color,
                        file.color,
                        file.color,
                        file.color,
                      ],
                    }),
                    backgroundSize: "cover",
                    backgroundPosition: "center center",
                    backgroundRepeat: "no-repeat",
                    backgroundBlendMode: "lighten",
                  }}
                >
                  <div
                    className="w-full h-full absolute left-0 top-0 items-center justify-center rounded-[16px]"
                    onClick={() => handleMultiFileSelect(file)}
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                      display: isSelected(file) && select ? "flex" : "none",
                    }}
                  >
                    <div className="py-3 px-3 rounded-full bg-green-400">
                      <DoneIcon />
                    </div>
                  </div>

                  <span className="flex  justify-between items-center">
                    <button
                      onClick={() =>
                        select
                          ? handleMultiFileSelect(file)
                          : openFileInfoDrawer(file)
                      }
                    >
                      <b className="text-gray-500 font-serif text-xs border-2 border-gray-500 rounded-full px-[5px] mx-1">
                        i
                      </b>
                    </button>

                    <span style={{ fontSize: 20 }} className="flex">
                      <div onClick={() => handleLockChange(file)}>
                        {file?.isLocked ? (
                          <LockIcon fontSize="inherit" />
                        ) : (
                          <LockOpenIcon fontSize="inherit" />
                        )}
                      </div>

                      <div
                        onClick={() => handlePinChange(file)}
                        className="ml-1"
                      >
                        {file?.isPinned ? (
                          <PushPinIcon fontSize="inherit" />
                        ) : (
                          <PushPinOutlinedIcon fontSize="inherit" />
                        )}
                      </div>

                      {(file?.owner === profileData?.id ||
                        profileData?.role_priv === "org_admin") && (
                        <>
                          <button
                            className=""
                            onClick={(event) => {
                              handleMenuClick(event);
                              setMenuFile(file);
                            }}
                          >
                            <img
                              src={threeDots}
                              height={25}
                              width={25}
                              alt=""
                            />
                          </button>

                          <Menu
                            id="basic-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                            MenuListProps={{
                              "aria-labelledby": "basic-button",
                            }}
                            PaperProps={{
                              style: {
                                border: "1px solid [#11182626]",
                                boxShadow: "0px 2px 3px rgba(0, 0, 0, 0.1)",
                                borderRadius: "6px",
                              },
                            }}
                          >
                            <MenuItem style={{ padding: "0px 10px" }}>
                              <FileShare
                                menuFile={menuFile}
                                closeMenu={handleClose}
                              />
                            </MenuItem>

                            <MenuItem
                              style={{ padding: "0px 10px" }}
                              onClick={() => handleSingleFileDownload(menuFile)}
                            >
                              Download
                            </MenuItem>

                            <MenuItem
                              style={{ padding: "0px 10px", color: "#D1293D" }}
                            >
                              <DeleteAlert
                                fileName={menuFile.name}
                                owner={menuFile.owner}
                                id={menuFile.id}
                                remove={removeFile}
                                size={menuFile.size}
                                mimeType={menuFile.mimetype}
                                profilePic={menuFile.profilePic}
                                downloadUrl={menuFile.downloadUrl}
                                currVersion={menuFile.currVersion}
                                versions={menuFile.versions}
                                onRecycled={(fileId) => {
                                  // Optimistically hide from local render
                                  setRecycleFiles((prev) => [
                                    ...prev,
                                    { fileId },
                                  ]);
                                  // Update the store so DepartmentPage
                                  // recomputes totalPages and backfills from
                                  // the next page immediately
                                  if (typeof onFileDeleted === "function") {
                                    onFileDeleted(fileId);
                                  }
                                }}
                              />
                            </MenuItem>

                            <MenuItem style={{ padding: "0px 10px" }}>
                              <RenameFile
                                file={menuFile}
                                closeMenu={handleClose}
                              />
                            </MenuItem>

                            {profileData.id === menuFile.owner && (
                              <MenuItem style={{ padding: "0px 10px" }}>
                                <SetPassword file={menuFile} />
                              </MenuItem>
                            )}

                            {profileData.id === menuFile.owner &&
                              menuFile.hasPassword && (
                                <MenuItem
                                  style={{ padding: "0px 10px" }}
                                  onClick={() => handleRemovePassword(menuFile)}
                                >
                                  Remove Password
                                </MenuItem>
                              )}

                            {profileData.id === menuFile.owner && (
                              <MenuItem style={{ padding: "0px 10px" }}>
                                <ShowVersions
                                  file={menuFile}
                                  openFile={(fileId) => {
                                    setIsFileViewOpen(false); // unmount current editor
                                    setSelectedFileInfo((prev) => ({
                                      ...prev,
                                      currVersion: fileId,
                                    }));
                                    setTimeout(
                                      () => setIsFileViewOpen(true),
                                      0
                                    ); // remount fresh → re-fetch
                                    handleClose();
                                  }}
                                  closeMenu={handleClose}
                                />
                              </MenuItem>
                            )}
                          </Menu>
                        </>
                      )}
                    </span>
                  </span>

                  <div
                    onClick={() =>
                      select
                        ? handleMultiFileSelect(file)
                        : fileAuthenticated || !file.hasPassword
                          ? openDrawer(file)
                          : openPasswordDialog(file)
                    }
                  >
                    <span className="flex justify-center items-center">
                      {/* Use the getIconByExtension function to determine the correct SVG */}
                      <img
                        src={getIconByMimeType(file.mimetype)}
                        alt="File Preview"
                        className={`rounded-md my-4 ${location.pathname === "/chat" ? "h-8" : "auto"}`}
                      />
                    </span>

                    <span>
                      <h5 className="font-semibold line-clamp-1 text-gray-700 text-sm mb-1">
                        {file?.name?.split("_TS=")[0]}
                      </h5>

                      <span className="flex flex-row justify-between items-center">
                        <span>
                          <h6 className="font-semibold">
                            {/* <span className="text-xs font-bold text-[#676767]">
                            File size:
                          </span>{" "} */}
                            <span className="text-xs font-bold text-[10px] text-[#676767]">
                              {file.size}
                            </span>
                          </h6>
                        </span>

                        <Avatar
                          src={
                            file.profilePic ||
                            (file.owner_id === profileData?.id
                              ? profileData?.profilePictureUrl
                              : "")
                          }
                          alt="owner pic"
                          sx={{
                            width: 24,
                            height: 24,
                            border: "1px solid silver",
                          }}
                          className={`${darkMode && "border border-white "}`}
                        />
                      </span>
                    </span>
                  </div>
                </div>
              );
            })}
      </div>

      {isFileInfoOpen && (
        <FileInfo
          fileInfo={selectedFileInfo}
          closeDrawer={closeFileInfoDrawer}
          sharedFileInfo={sharedFileInfo}
          handleLockChange={() => handleLockChange(selectedFileInfo)}
        />
      )}

      {isFileViewOpen && (
        <FileView
          fileInfo={selectedFileInfo}
          closeDrawer={closeDrawer}
          sharedFileInfo={sharedFileInfo}
        />
      )}

      {downloadLoading && (
        <Dialog
          open={downloadLoading}
          PaperProps={{
            style: {
              borderRadius: "5px",
              maxWidth: "80%",
              width: "auto",
              maxHeight: "80%",
            },
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent className="bg-white">
            <div className="loader-overlay flex flex-col items-center justify-center">
              <CircularProgress className="mb-5" />
              <p>Downloading file...</p>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default FilesPage;
